import React from 'react';
import { withRouter } from 'react-router-dom';
import { isEmpty, get, find, isEqual } from 'lodash';
import { connect } from 'react-redux';
import { REDUCER } from 'constants/entity-names';
import * as errorOverlay from 'utils/errorOverlay';

// local dependencies
import { DASHBOARD } from 'constants/route-paths';
import { MemberManagementWidgetService } from 'services/memberManagementWidget';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { memberManagementWidget as MemberManagementWidgetView } from 'ui/views/widgets/memberManagementWidget';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { messageBus } from 'core/modules/message-bus';
import { getCheckoutState, clearRxCheckoutState } from 'resolvers/checkoutState';
import { updateMemberManagementState } from 'resolvers/memberManagementState';
import { getAppSyncData } from 'utils/getAppSyncData';
import { resolveMemberDetailSelection } from 'utils/memberDetailSelection';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberManagementWidget } from 'ui/containers/MemberManagementWidget';
 *
 * // render
 * <MemberManagementWidget propName='propValue'/>
 */
class _MemberManagementWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        // bind event and methods
        //this.handleMemberSwitch = this.handleMemberSwitch.bind( this );
        this.refetchComponentData = this.refetchComponentData.bind( this );
        this.handleGenerateActionListLog = this.handleGenerateActionListLog.bind( this );
        this.onRouteStateChange = this.onRouteStateChange.bind( this );

        // listen to history changes
        this.unlisten = props.history.listen( this.onRouteStateChange );

        this.data = resolveMemberDetailSelection( this.props.location.state, {
            memberId: getAppSyncData( 'memberId' ),
            patientId: getAppSyncData( 'patientId' ),
            liveFallback: getAppSyncData( 'liveFallback' )
        } );
        const { memberId, patientId } = this.data;

        // get checkout state
        const checkoutState = getCheckoutState();

        // if current `memberId` is different from the one stored in checkout state
        if ( ! isEqual( get( checkoutState, 'memberId', '' ), memberId ) ) {

            // clear checkout state
            clearRxCheckoutState();
        }

        if ( ! isEqual( get( checkoutState, 'patientId', '' ), patientId ) ) {

            // clear checkout state
            clearRxCheckoutState();
        }

        // Persist identity and provenance atomically; never retain a previous patient's hint.
        updateMemberManagementState( this.data );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // create service instance
        this.memberManagementWidgetService = new MemberManagementWidgetService();
        this.requestGeneration = 0;
        this.requestActive = true;

        // listen to message bus events
        messageBus.on( MESSAGE_BUS_EVENTS.MEMBER_MANAGEMENT_REFRESH, this.refetchComponentData, this );

        /*-----------*/

        // if - `memberId` is not present, redirect to the dashboard landing
        // else - make service request for the component data
        if( isEmpty( memberId ) && isEmpty( patientId )  ) {
            this.props.history.replace( DASHBOARD );
        } else {
            this.fetchComponentData(); // start component data fetch
        }
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'MemberManagementWidget.fetchComponentData()' );

        const generation = ++this.requestGeneration;

        // cancel ongoing request
        this.memberManagementWidgetService.cancel();

        const memberId = get( this.data, 'memberId', undefined );
        
        //Added patientId
        const patientId= get( this.data, 'patientId', undefined );

        // get status and service data
        const { status, data } = await this.memberManagementWidgetService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: {
                    memberId: memberId,
                    patientId: patientId,
                    liveFallback: this.data.liveFallback
                }
            }
        } );

        if( ! this.requestActive || generation !== this.requestGeneration ) return;

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( () => ! this.requestActive || generation !== this.requestGeneration ? null : ( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } ) );
        } else {
            this.setState( () => ! this.requestActive || generation !== this.requestGeneration ? null : ( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } ), () => {

                if( ! this.requestActive || generation !== this.requestGeneration ) return;

                // generate off-call action-list log (on mount)
                if( isEmpty( this.props.callState ) ) {
                    this.handleGenerateActionListLog();
                }
            } );
        }
    }

    /**
     * @desc Re-fetch component data
     */
    refetchComponentData() {
        log.debug( 'MemberManagementWidget.refetchComponentData()' );

        if( ! this.requestActive ) return;

        const generation = ++this.requestGeneration;

        this.setState( () => ! this.requestActive || generation !== this.requestGeneration ? null : ( {
            isFetching: true,
            networkStatus: undefined,
        } ), () => {
            if( this.requestActive && generation === this.requestGeneration ) this.fetchComponentData();
        } );
    }

    onRouteStateChange( payload ) {
        const routeState = get( payload, 'state' );
        if( ! routeState || ( ! Object.prototype.hasOwnProperty.call( routeState, 'memberId' ) &&
            ! Object.prototype.hasOwnProperty.call( routeState, 'patientId' ) ) ) {
            return;
        }
        const nextSelection = resolveMemberDetailSelection( routeState, this.data );
        const changed = ! isEqual( nextSelection, this.data );
        this.data = nextSelection;
        updateMemberManagementState( this.data );
        if( changed ) {
            const generation = ++this.requestGeneration;
            this.memberManagementWidgetService.cancel();
            this.setState( () => ! this.requestActive || generation !== this.requestGeneration ? null : ( {
                isFetching: true,
                networkStatus: undefined,
                componentData: undefined,
            } ), () => {
                if( this.requestActive && generation === this.requestGeneration ) this.fetchComponentData();
            } );
        }
    }

    /**
     * @desc Make a request to create off-call action-list log.
     */
    async handleGenerateActionListLog() {
        log.debug( 'MemberManagementWidget.handleGenerateActionListLog()' );

        const generation = this.requestGeneration;
        if( ! this.requestActive ) return;

        const memberId = get( this.data, 'memberId', undefined );
        const patientId = get( this.data, 'patientId', undefined );

        // get action item
        const action = find( get( this.state, 'componentData.onMountActions' ), { name: 'generateActionListLog' } );

        // return if `action` is missing
        if( isEmpty( action ) ) {
            return; //no-op
        }

        // cancel ongoing request
        this.memberManagementWidgetService.cancel();

        // get status and service data
        const { status } = await this.memberManagementWidgetService.generateActionListLog( {
            url: action.url,
            data: {
                context: {
                    memberId: memberId,
                    patientId: patientId,
                    liveFallback: this.data.liveFallback
                }
            }
        } );

        // check if response status was success
        if( this.requestActive && generation === this.requestGeneration && SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            errorOverlay.open(); // show error
        }
    }

    // /**
    //  * @desc When dependent is witched
    //  */
    // handleMemberSwitch( member = {} ) {
    //     log.debug( 'MemberManagementWidget.handleMemberSwitch()' );

    //     // switch to member details route
    //     this.props.history.push( {
    //         pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
    //         search: searchQuery.stringify( {
    //             memberId: member.id,
    //         } ),
    //     } );
    // }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.requestActive = false;
        ++this.requestGeneration;
        this.memberManagementWidgetService.cancel(); // cancel ongoing requests

        // remove message bus events
        messageBus.off( MESSAGE_BUS_EVENTS.MEMBER_MANAGEMENT_REFRESH, this.refetchComponentData, this );

        this.unlisten();
    }

    /**
     * @desc Render a React view component
     */
    render() {

        const memberId = get( this.data, 'memberId', undefined );
        const patientId = get( this.data, 'patientId', undefined );

        return (
            <MemberManagementWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                memberId={ memberId }
                patientId={ patientId }
                refreshKey={ JSON.stringify( [ memberId, patientId, this.data.liveFallback ] ) }
                { ...get( this.state, 'componentData', {} ) }
                onMemberSwitch={ this.handleMemberSwitch }
                onGenerateActionListLog={ this.handleGenerateActionListLog }
                requestPayload = { {
                    context: { memberId, patientId, liveFallback: this.data.liveFallback }
                } }
            />
        );
    }
}

// set display name
_MemberManagementWidget.displayName = '_MemberManagementWidget';

// set default props
_MemberManagementWidget.defaultProps = {};

// prop types
_MemberManagementWidget.propTypes = {};

/*********************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// return component with acccess to Redux store
export const MemberManagementWidget = withRouter( connect( mapStateToProps, null )( _MemberManagementWidget ) );
